import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Heading,
  InfoBox,
  Input,
  RefreshButton,
  Select,
  Stack,
  Table,
  Text,
  notify,
} from "components/ui";
import DeleteConfirmDialog from "components/common/DeleteConfirmDialog";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdSearch, MdVisibility } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  CustomerCompanyMember,
  customerCompanyService,
} from "services/customerCompanyService";
import {
  CustomerCompanyMemberRole,
  customerCompanyAdminService,
} from "services/customerCompanyAdminService";
import { getThemeColor } from "theme/colors";
import { formatDate } from "./format";

const MEMBERS_PAGE_SIZE = 10;

interface MembersTabProps {
  customerCompanyId: string;
}

/**
 * The customers belonging to this company, with the role and access controls a Customer Admin has
 * in the Portal's Members tab. Server-paged and server-searched.
 */
const MembersTab = ({ customerCompanyId }: MembersTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [members, setMembers] = useState<CustomerCompanyMember[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pendingMemberId, setPendingMemberId] = useState<string | null>(null);
  const [disableTarget, setDisableTarget] =
    useState<CustomerCompanyMember | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await customerCompanyService.getMembers(
        customerCompanyId,
        {
          search: search || undefined,
          page,
          pageSize: MEMBERS_PAGE_SIZE,
        },
      );
      setMembers(result.items);
      setTotal(result.total);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customerCompanies.members.loadFailed", {
            defaultValue: "Failed to load members.",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, search, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / MEMBERS_PAGE_SIZE));

  const applyMemberUpdate = (
    memberId: string,
    changes: Partial<CustomerCompanyMember>,
  ) =>
    setMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, ...changes } : m)),
    );

  const reportFailure = (err: any) =>
    notify({
      type: "error",
      title: t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      description: err.response?.data?.error,
    });

  const handleRoleChange = async (
    member: CustomerCompanyMember,
    role: CustomerCompanyMemberRole,
  ) => {
    if (role === member.role) return;
    setPendingMemberId(member.id);
    try {
      const updated = await customerCompanyAdminService.changeMemberRole(
        customerCompanyId,
        member.id,
        role,
      );
      applyMemberUpdate(member.id, { role: updated.role });
      notify({
        type: "success",
        title: t("app.customerCompanies.members.roleChanged", {
          defaultValue: "Member role updated.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setPendingMemberId(null);
    }
  };

  const applyDisabled = async (
    member: CustomerCompanyMember,
    disabled: boolean,
  ) => {
    setPendingMemberId(member.id);
    try {
      const updated = await customerCompanyAdminService.setMemberDisabled(
        customerCompanyId,
        member.id,
        disabled,
      );
      applyMemberUpdate(member.id, { isDisabled: updated.isDisabled });
      notify({
        type: "success",
        title: t("app.customerCompanies.members.statusChanged", {
          defaultValue: "Member access updated.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setPendingMemberId(null);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  return (
    <Card.Root
      borderWidth="1px"
      borderColor={getThemeColor("card.border")}
      borderRadius="md"
    >
      <Card.Body>
        <Stack gap={3}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Stack gap={1}>
              <Heading size="sm">
                {t("app.customerCompanies.members.title", {
                  defaultValue: "Members",
                })}
              </Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.members.subtitle", {
                  defaultValue: "Customers that belong to this company.",
                })}
              </Text>
            </Stack>
            <RefreshButton onClick={load} loading={loading} />
          </Flex>

          <HStack gap={2}>
            <Input
              placeholder={t(
                "app.customerCompanies.members.searchPlaceholder",
                {
                  defaultValue: "Search by id, name, or email",
                },
              )}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              flex={1}
            />
            <Button intent="primary" onClick={handleSearch}>
              <MdSearch /> {t("app.common.search", { defaultValue: "Search" })}
            </Button>
            {search && (
              <Button intent="secondary" onClick={handleSearchClear}>
                {t("app.common.clearFilters", { defaultValue: "Clear" })}
              </Button>
            )}
          </HStack>

          {error && (
            <InfoBox variant="error">
              <Text color="red.700">{error}</Text>
            </InfoBox>
          )}

          {loading ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerCompanies.members.loading", {
                defaultValue: "Loading members…",
              })}
            </Text>
          ) : members.length === 0 ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {search
                ? t("app.customerCompanies.members.noResults", {
                    defaultValue: "No members match your search.",
                  })
                : t("app.customerCompanies.members.empty", {
                    defaultValue: "No customers in this company yet.",
                  })}
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.members.columns.id", {
                        defaultValue: "Customer ID",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.members.columns.name", {
                        defaultValue: "Name",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.members.columns.email", {
                        defaultValue: "Email",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.members.columns.role", {
                        defaultValue: "Role",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.members.columns.status", {
                        defaultValue: "Status",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customerCompanies.members.columns.lastSeen", {
                        defaultValue: "Last seen",
                      })}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.common.actions", { defaultValue: "Actions" })}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {members.map((m) => (
                    <Table.Row key={m.id}>
                      <Table.Cell>
                        <Text fontFamily="mono" fontSize="xs">
                          {m.customerId}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>{m.customerName ?? "—"}</Table.Cell>
                      <Table.Cell>{m.customerEmail ?? "—"}</Table.Cell>
                      <Table.Cell>
                        <Select
                          value={[m.role]}
                          disabled={pendingMemberId === m.id}
                          onValueChange={({ value }) =>
                            handleRoleChange(
                              m,
                              (value[0] ?? m.role) as CustomerCompanyMemberRole,
                            )
                          }
                          items={[
                            {
                              value: "Admin",
                              label: t(
                                "app.customerCompanies.members.role.admin",
                                {
                                  defaultValue: "Admin",
                                },
                              ),
                            },
                            {
                              value: "Member",
                              label: t(
                                "app.customerCompanies.members.role.member",
                                {
                                  defaultValue: "Member",
                                },
                              ),
                            },
                          ]}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          colorPalette={m.isDisabled ? "gray" : "green"}
                          variant="subtle"
                        >
                          {m.isDisabled
                            ? t("app.customerCompanies.members.disabled", {
                                defaultValue: "Disabled",
                              })
                            : t("app.customerCompanies.members.active", {
                                defaultValue: "Active",
                              })}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>{formatDate(m.lastUpdatedAt)}</Table.Cell>
                      <Table.Cell>
                        <HStack gap={1}>
                          <Button
                            intent="ghost"
                            onClick={() => navigate(`/admin/customers/${m.id}`)}
                          >
                            <MdVisibility />{" "}
                            {t("app.common.view", { defaultValue: "View" })}
                          </Button>
                          {m.isDisabled ? (
                            <Button
                              intent="ghost"
                              disabled={pendingMemberId === m.id}
                              onClick={() => applyDisabled(m, false)}
                            >
                              {t("app.customerCompanies.members.enable", {
                                defaultValue: "Enable",
                              })}
                            </Button>
                          ) : (
                            <Button
                              intent="ghost"
                              disabled={pendingMemberId === m.id}
                              onClick={() => setDisableTarget(m)}
                            >
                              {t("app.customerCompanies.members.disable", {
                                defaultValue: "Disable",
                              })}
                            </Button>
                          )}
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}

          {total > MEMBERS_PAGE_SIZE && (
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <Text fontSize="xs" color="var(--text-secondary-color)">
                {t("app.customerCompanies.members.pageStatus", {
                  defaultValue: "Showing {{from}}–{{to}} of {{total}}",
                  from: (page - 1) * MEMBERS_PAGE_SIZE + 1,
                  to: Math.min(page * MEMBERS_PAGE_SIZE, total),
                  total,
                })}
              </Text>
              <HStack gap={2}>
                <Button
                  intent="ghost"
                  disabled={page === 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t("app.common.previous", { defaultValue: "Previous" })}
                </Button>
                <Text fontSize="xs" color="var(--text-secondary-color)">
                  {t("app.common.pageOf", {
                    defaultValue: "Page {{page}} of {{totalPages}}",
                    page,
                    totalPages,
                  })}
                </Text>
                <Button
                  intent="ghost"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t("app.common.next", { defaultValue: "Next" })}
                </Button>
              </HStack>
            </Flex>
          )}
        </Stack>
      </Card.Body>

      <DeleteConfirmDialog
        isOpen={disableTarget !== null}
        onClose={() => setDisableTarget(null)}
        onConfirm={() => {
          const target = disableTarget;
          setDisableTarget(null);
          if (target) void applyDisabled(target, true);
        }}
        title={t("app.customerCompanies.members.disable", {
          defaultValue: "Disable",
        })}
        message={t("app.customerCompanies.members.confirmDisable", {
          defaultValue:
            "This member will no longer be able to sign in and their active sessions will be revoked. Their history is kept. Continue?",
        })}
      />
    </Card.Root>
  );
};

export default MembersTab;
